import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, computed, inject, model, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { EditLinks } from '../../../../@core/constants/edit-links';
import { createSettingsSignal } from '../../../../@core/services/settings.service';
import { ColumnConfigEx, ColumnDefinitionEx } from '../../../../@core/types/column-config-types-extended';
import { Employee, Task, TaskSearchInput, TaskStatus } from '../../../../generated/generated-types';
import { BaseListWrapperComponent } from '../../../../shared/components/base-list-wrapper/base-list-wrapper.component';
import { NewBaseListComponent } from '../../../../shared/components/new-base-list-component/new-base-list-component';
import { PhxCheckboxComponent } from '../../../../shared/components/phx-input-checkbox/phx-input-checkbox.component';
import { EmployeeDropdownComponent } from '../../../../shared/controls/employeer-dropdown/employee-dropdown.component';
import { AuthService } from '../../../../shared/services/auth.service';
import { TaskService } from '../../services/task.service';
import { TaskEditWidgetComponent } from '../task-edit-widget/task-edit-widget.component';
import { TaskPriorityColumnWidgetComponent } from '../task-priority-column-widget/task-priority-column-widget.component';
import { TaskRelatedEntityColumnWidgetComponent } from '../task-related-entity-column-widget/task-related-entity-column-widget.component';
import { TaskStatusColumnWidgetComponent } from '../task-status-column-widget/task-status-column-widget.component';
import { TaskTitleColumnWidgetComponent } from '../task-title-column-widget/task-title-column-widget.component';

const S_TASK_SHOW_OPEN_TASKS_ONLY = "S_TASK_SHOW_OPEN_TASKS_ONLY";

@Component({
    selector: 'app-task-list-widget',
    templateUrl: './task-list-widget.component.html',
    providers: [TaskService],
    standalone: true,
    imports: [CommonModule, BaseListWrapperComponent, PhxCheckboxComponent, EmployeeDropdownComponent, FormsModule, TranslatePipe]
})
export class TaskListWidgetComponent extends NewBaseListComponent<Task> implements AfterViewInit {

    protected readonly authService = inject(AuthService);

    readonly employeeDropdown = viewChild<EmployeeDropdownComponent>('employeeDropdown');

    readonly selectedEmployeeId = signal<string | null>(null);
    readonly showOpenTasksOnly = createSettingsSignal(S_TASK_SHOW_OPEN_TASKS_ONLY, false);
    readonly customSearchInput = signal<Partial<TaskSearchInput>>({});
    readonly filterByAttachedEntity = computed(() => !!this.customSearchInput().attachedEntityType);
    readonly autoSelectMyEmployee = model(false);

    constructor() {
        super('Task', inject(TaskService));
        this.doubleClickBaseUrl = EditLinks.TASK;
        this.addInDetail = true;
        this.dialogEditMode = true;
        this.addInDialog = true;

        this.outputComponentChange.subscribe(() => this.refresh());
    }

    ngAfterViewInit(): void {
        if (this.autoSelectMyEmployee() && !this.filterByAttachedEntity())
            this.selectMyEmployee();
        super.ngAfterViewInit();
    }

    setCustomSearchInput(searchInput: Partial<TaskSearchInput>, refresh = true): void {
        this.customSearchInput.set(searchInput);
        if (refresh) {
            void this.refreshWhenEntityFilterReady();
        }
    }

    private async refreshWhenEntityFilterReady(): Promise<void> {
        if (!this.filterByAttachedEntity()) {
            await this.refresh();
            return;
        }

        if (!this.customSearchInput().attachedEntityId) {
            return;
        }

        await this.waitForListReady();
        await this.refresh(true);
    }

    private waitForListReady(): Promise<void> {
        if (this.tableInitDone() && this.columnConfigIsSet()) {
            return Promise.resolve();
        }

        return new Promise(resolve => {
            let attempts = 0;
            const maxAttempts = 300;
            const check = () => {
                if (this.tableInitDone() && this.columnConfigIsSet()) {
                    resolve();
                    return;
                }
                if (++attempts >= maxAttempts) {
                    console.warn('[TaskListWidget] Timed out waiting for list ready state');
                    resolve();
                    return;
                }
                requestAnimationFrame(check);
            };
            check();
        });
    }

    override showEditDialog(rowData?: Task, _index?: number) {
        const entityFilter = this.customSearchInput();
        if (this.filterByAttachedEntity()) {
            rowData = rowData ?? {} as Task;
            rowData.attachedEntityType = entityFilter.attachedEntityType;
            rowData.attachedEntityId = entityFilter.attachedEntityId;

            if (!rowData.attachedEntityType) {
                this.baseServices.notificationService.error('No attached entity type set');
                return;
            }

            if (!rowData.attachedEntityId) {
                this.baseServices.notificationService.error('No attached entity id set');
                return;
            }
        }

        lastValueFrom(this.baseServices.modalService.fromComponent(TaskEditWidgetComponent, {
            size: 'md',
            closable: true,
            maximizable: false,
            title: this.baseServices.i18nService.translate(rowData?.id ? _('task.edit-task') : _('task.add-task')),
            locals: {
                detailModel: rowData
            }
        })).then(() => {
            this.refresh();
        });
    }

    override async setColumnConfig(colConfig: ColumnConfigEx): Promise<void> {
        if (!colConfig.columns.some((column: ColumnDefinitionEx) => column.field === 'statusTemplate')) {
            let customCol: ColumnDefinitionEx = {
                id: uuidv4(),
                dataType: 'custom',
                field: 'statusTemplate',
                // customHeaderName: this.baseServices.i18nService.translate(_('entities.Task.status')),
                header: this.baseServices.i18nService.translate(_('entities.Task.status')),
                width: 100,
                visible: true,
                deletable: false,
                sortable: false,
            };

            colConfig.columns.unshift(customCol);
            await this.baseServices.columnService.saveItem(colConfig, true);
        }

        if (!colConfig.columns.some((column: ColumnDefinitionEx) => column.field === 'relatedEntityTemplate')) {
            const relatedEntityCol: ColumnDefinitionEx = {
                id: uuidv4(),
                dataType: 'custom',
                field: 'relatedEntityTemplate',
                header: this.baseServices.i18nService.translate(_('task.related-entity')),
                width: 180,
                visible: true,
                deletable: false,
                sortable: false,
            };

            const statusIndex = colConfig.columns.findIndex((col: ColumnDefinitionEx) => col.field === 'statusTemplate');
            if (statusIndex >= 0) {
                colConfig.columns.splice(statusIndex + 1, 0, relatedEntityCol);
            } else {
                colConfig.columns.unshift(relatedEntityCol);
            }
            await this.baseServices.columnService.saveItem(colConfig, true);
        }

        if (!colConfig.columns.some((column: ColumnDefinitionEx) => column.field === 'priorityTemplate')) {
            let priorityCol: ColumnDefinitionEx = {
                id: uuidv4(),
                dataType: 'TaskPriority',
                field: 'priorityTemplate',
                isEnum: true,
                customHeaderName: this.baseServices.i18nService.translate(_('entities.Task.priority')),
                header: this.baseServices.i18nService.translate(_('entities.Task.priority')),
                width: 100,
                visible: true,
                deletable: false,
                sortable: false,
            } as any;

            // Insert after statusTemplate column
            const statusIndex = colConfig.columns.findIndex((col: ColumnDefinitionEx) => col.field === 'statusTemplate');
            if (statusIndex >= 0) {
                colConfig.columns.splice(statusIndex + 1, 0, priorityCol);
            } else {
                colConfig.columns.unshift(priorityCol);
            }
            await this.baseServices.columnService.saveItem(colConfig, true);
        }

        colConfig.columns.forEach((col: ColumnDefinitionEx) => {
            if (col.field == 'statusTemplate') {
                col.deletable = false;
                col.outputComponent = TaskStatusColumnWidgetComponent;
            }
            if (col.field == 'relatedEntityTemplate') {
                col.deletable = false;
                col.outputComponent = TaskRelatedEntityColumnWidgetComponent;
            }
            if (col.field == 'priorityTemplate') {
                col.deletable = false;
                col.outputComponent = TaskPriorityColumnWidgetComponent;
                // Ensure enum filter is enabled
                col.isEnum = true;
                (col as any).type = 'TaskPriority';
                if (col.dataType !== 'TaskPriority') {
                    col.dataType = 'TaskPriority';
                }
            }
            if (col.field == 'title') {
                col.outputComponent = TaskTitleColumnWidgetComponent;
            }
        });

        await super.setColumnConfig(colConfig);

        if (this.filterByAttachedEntity() && this.customSearchInput().attachedEntityId && !this.initialFetchExecuted) {
            await this.refresh(true);
        }
    }

    override getSearchInputForQuery() {
        const searchInput = {
            ...super.getSearchInputForQuery() as TaskSearchInput,
            ...this.customSearchInput(),
        };

        if (!this.filterByAttachedEntity()) {
            if (this.selectedEmployeeId()) {
                searchInput.assignedEmployeeId = this.selectedEmployeeId();
            } else {
                delete searchInput.assignedEmployeeId;
            }
        } else {
            delete searchInput.assignedEmployeeId;
        }

        if (searchInput.searchFilter?.rules) {
            // Map priorityTemplate filter field to priority for backend filtering
            searchInput.searchFilter.rules.forEach(rule => {
                if (rule.field === 'priorityTemplate') {
                    rule.field = 'priority';
                }
            });
            searchInput.searchFilter.rules = searchInput.searchFilter.rules.filter(rule => rule.field !== 'status');
        }

        if (this.showOpenTasksOnly()) {
            searchInput.status = [TaskStatus.PENDING, TaskStatus.IN_PROGRESS];
        }
        else {
            delete searchInput.status;
        }

        return searchInput;
    }

    onEmployeeChange(employee: Employee | null) {

        if (this.selectedEmployeeId() === employee?.id)
            return;

        this.selectedEmployeeId.set(employee?.id || null);
        if (this.initialFetchExecuted)
            this.refresh();
    }

    selectMyEmployee() {
        const currentUser = this.authService.getCurrentUser();
        if (currentUser?.activeEmployee?.id) {
            const employeeId = currentUser.activeEmployee.id;
            this.selectedEmployeeId.set(employeeId);
            // Load and set the employee in the dropdown
            const dropdown = this.employeeDropdown();
            if (dropdown) {
                dropdown.loadItemByIdAndSetSelected(employeeId);
            }
            if (this.initialFetchExecuted)
                this.refresh();
        }
    }

    onOpenTasksOnlyChange(checked: boolean) {
        this.showOpenTasksOnly.set(checked);
        this.refresh();
    }

} 
